import React from "react";
import config, { getApiUrl } from "./config";
import authService from "./AuthService";
import { Params } from "react-router-dom";
import "./Endpoint.css";
import { withRouter } from "./Utils";
import LiveEndpointView from "./LiveEndpointView";
import toast from "react-hot-toast";
import ReDeploy from "./ReDeploy";
import EventView from "./EventsView";
import ConfigurationEditor from "./ConfigurationEditor";
import HalStatusView from "./HalStatusView";
import { ReactComponent as CodeIcon } from "./code.svg";

type Space = {
  label: string;
  type: string;
  metadata: Map<string, any>;
};

type Building = {
  metadata: Map<string, any>;
  spaces: Array<Space>;
};

export type Appliance = {
  id: string;
  friendlyName: string;
  applianceReference: string;
  component: string;
  version: string;
  configuration: Map<string, string>;
};

export type ApplianceReference = {
  id: string;
  friendlyName: string;
  reference: string;
};

export type Process = {
  name: string;
  category: string;
  appliances: Array<Appliance>;
  applianceReferences?: Array<ApplianceReference>;
};

export type Description = {
  building: Building;
  processes: Array<Process> | undefined;
};

type EndpointDetails = {
  endpointId: string;
  friendlyName: string;
  online: boolean;
  state: string;
  coreVersion: string;
  platform: string;
  serialNumber: string;
  halConfiguration: {
    pending: boolean;
    content: HalConfig;
  };
  description: {
    pending: boolean;
    content: Description;
  };
  applicationConfiguration: {
    pending: boolean;
    content: any;
  };
  values: any;
};

type ConfigurationParameters = {
  friendlyName: string;
  key: string;
  type: string;
  constraint: string;
  defaultValue: string;
  description: string;
};

export type DriverImageDetails = {
  imageReference: string;
  friendlyName: string;
  version: string;
  architectures: Array<string>;
  description: string;
  changeLog: string;
  supportedAppliances: Array<string>;
  configurationParameters: Array<ConfigurationParameters>;
};

type DriverImage = {
  id: number;
  imageReference: string;
  friendlyName: string;
  version: string;
  architectures: Array<string>;
  supportedAppliances: Array<string>;
};

// The HAL configuration (@HALCONFIG): flat keys, e.g. "io.interfaces.X11": "MODBUS_RTU",
// "io.interfaces.X11.baudRate": "9600" (see the HAL component's interfaces).
export type HalConfig = Record<string, string>;

export type ApplianceStatus = {
  id: string;
  friendlyName: string;
  state: string;
  deviceInformation?: Record<string, string>;
};

type EndpointStatusMessage = {
  state: string;
  coreVersion: string;
  platform: string;
  serialNumber: string;
  halConfiguration: object;
  description: Description;
  appliances: Array<ApplianceStatus>;
};

type ApplicationConfiguration = {
  id: string;
  friendlyName: string;
  component: string;
  version: string;
  state: string;
  configuration: object;
  metadata: object;
};

class PendingUpdate {
  private resolve_?: () => void;

  constructor(failed: () => void, timeout: number = 30000) {
    toast.promise(
      new Promise<void>((resolve, reject) => {
        this.resolve_ = resolve;
        setTimeout(() => {
          reject();
          failed();
        }, timeout);
      }),
      {
        loading: <span>Waiting for core update to start...</span>,
        success: (
          <span>
            Successfully started core update. The endpoint should come back
            online in a few minutes.
          </span>
        ),
        error: <span>Failed to start core update!</span>,
      },
    );
  }

  resolve() {
    if (this.resolve_) this.resolve_();
  }
}

type EndpointProperties = {
  params?: Readonly<Params>;
};

enum Editor {
  None,
  ManualDescription,
  ManualHalConfiguration,
  ManualApplicationConfiguration,
  ReDeploy,
}

type EndpointState = {
  endpointId: string;
  details: EndpointDetails | undefined;
  pendingUpdate?: PendingUpdate;
  description: Description | undefined;
  driverImages: Array<DriverImageDetails> | undefined;
  halConfiguration: HalConfig | undefined;
  descriptionVisible: boolean;
  openEditor: Editor;
  applianceStatuses: Array<ApplianceStatus>;
  applicationConfiguration: Array<ApplicationConfiguration> | undefined;
};

class Endpoint extends React.Component<EndpointProperties, EndpointState> {
  private eventViewRef = React.createRef<EventView>();

  constructor(props: EndpointProperties) {
    super(props);
    this.state = {
      endpointId: props.params!["endpointId"] || "",
      details: undefined,
      description: undefined,
      driverImages: undefined,
      halConfiguration: undefined,
      descriptionVisible: true,
      openEditor: Editor.None,
      applianceStatuses: [],
      applicationConfiguration: undefined,
    };
  }

  componentDidMount() {
    this.getEndpoint();
    this.getEndpointStatus();
    this.getEndpointDescription();
    this.getDriverImageList(); // calls this.getDriverImageDetails() for each driver image
    this.getHALConfiguration();
  }

    private getEndpoint() {
        fetch(getApiUrl() + "/v1/endpoint/" + this.state.endpointId + "?includeHALConfiguration=true&includeDescription=true&includeApplianceValues=true&includeApplianceConfiguration=true&includeApplicationConfiguration=true", {
            method: "GET",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(async response => {
                if (response.ok) {
                    return await response.json() as Promise<EndpointDetails>
                } else if (response.status === 404) {
                    const body = await response.json()
                    toast.error("Error 404: " + body.message)
                }
            })
            .then((response) => {
                let applicationConfiguration = response!.applicationConfiguration.content
                applicationConfiguration.forEach((appConfig: ApplicationConfiguration) => {
                    if (appConfig.configuration === undefined) appConfig.configuration = {};
                });
                this.setState({
                    details: response,
                    applicationConfiguration: applicationConfiguration
                })
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private getEndpointStatus() {
        fetch(getApiUrl() + "/v1/endpoint/" + this.state.endpointId + "/status", {
            method: "GET",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(async response => {
                if (response.ok) {
                    return await response.json() as Promise<EndpointStatusMessage>
                } else if (response.status === 404) {
                    const body = await response.json()
                    console.error("Error 404: " + body.message)
                }
            })
            .then((response) => {
                this.setState({
                    applianceStatuses: response!.appliances
                })
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private getEndpointDescription() {
        fetch(getApiUrl() + "/v1/endpoint/" + this.state.endpointId + "/description?includeApplianceConfiguration=true", {
            method: "GET",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(async response => {
                if (response.ok) {
                    return await response.json() as Promise<Description>
                } else if (response.status === 404) {
                    const body = await response.json()
                    toast.error("Error 404: " + body.message)
                }
            })
            .then((response) => {
                this.setState({
                    description: response
                })
            })
            .catch((error) => {
                console.error(error);
            });
    }

    private getDriverImageList() {
        fetch(getApiUrl() + "/v1/component/appliance/", {
            method: "GET",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => response.json() as Promise<Array<DriverImage>>)
            .then((response) => {
                const promises = response.map((driverImage) => (
                    this.getDriverImageDetails(driverImage.imageReference, driverImage.version)
                ))

        // wait for all requests to be completed
        Promise.all(promises)
          .then((driverImageDetails) => {
            const updatedDriverImages = [
              ...(this.state.driverImages || []),
              ...driverImageDetails,
            ];

            // update state
            this.setState({
              driverImages: updatedDriverImages,
            });
          })
          .catch((error) => {
            console.error(error);
          });
      })
      .catch((error) => {
        console.error(error);
      });
  }

    private async getDriverImageDetails(image: string, version: string): Promise<DriverImageDetails> {
        try {
            const response = await fetch(getApiUrl() + "/v1/component/appliance/reference/" + image + "/version/" + version, {
                method: "GET",
                headers: [
                    ["Accept", "application/json"],
                    ["Authorization", authService.authHeader()]
                ],
            });
            if (response.ok) {
                return await (await response.json() as Promise<DriverImageDetails>);
            } else if (response.status === 404) {
                const body = await response.json()
                toast.error("Error 404: " + body.message)
                return {} as DriverImageDetails;
            } else {
                return {} as DriverImageDetails;
            }
        } catch (error) {
            console.error(error);
            return {} as DriverImageDetails;
        }
    }

    private getHALConfiguration() {
        fetch(getApiUrl() + "/v1/endpoint/" + this.state.endpointId + "/hal/configuration", {
            method: "GET",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(async response => {
                if (response.ok) {
                    return await response.json() as Promise<HalConfig>
                } else if (response.status === 404) {
                    const body = await response.json()
                    toast.error("Error 404: " + body.message)
                }
            })
            .then((response) => {
                this.setState({
                    halConfiguration: response
                })
            })
            .catch((error) => {
                toast.error("Failed to get HAL configuration");
            });
    }

  private showRedeployInstructions = () => {
    this.setState({
      openEditor: Editor.ReDeploy,
    });
  };

  private hideQrReader = () => {
    this.setState({
      openEditor: Editor.None,
    });
  };

  private deleteEndpoint = () => {
    const sure = window.confirm(
      "Are you sure you want to delete this endpoint?",
    );
    if (!sure) {
      return;
    }

        fetch(getApiUrl() + "/v1/endpoint/" + this.state.endpointId, {
            method: "DELETE",
            headers: [
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => {
                if (response.ok) {
                    window.location.href = "/endpoint";
                } else {
                    toast.error("Failed to delete endpoint");
                }
            })
            .catch((error) => {
                toast.error("Failed to delete endpoint");
            });
    }

  render() {
    return (
      <div className="Endpoint">
        <h2>{this.state.details?.friendlyName || this.state.endpointId}</h2>
        {this.renderDetails()}
      </div>
    );
  }

  renderDetails() {
    if (this.state.details !== undefined) {
      return (
        <div className="endpoint-details">
          <div className="properties">
            <div>Endpoint ID:</div>
            <div className="endpoint-state">
              {this.state.details.endpointId}
            </div>
            <div>Cloud connection status:</div>
            <div
              className={
                this.state.details.online
                  ? "endpoint-state online"
                  : "endpoint-state offline"
              }
            >
              {this.state.details.online ? "online" : "offline"}
            </div>
            <div>Endpoint state:</div>
            <div className="endpoint-state">{this.state.details.state}</div>
            <div>Core software version:</div>
            <div className="endpoint-state">
              {this.state.details.coreVersion || "-"}
            </div>
            <div>Platform:</div>
            <div className="endpoint-state">
              {this.state.details.platform || "-"}
            </div>
            <div>Serial number:</div>
            <div className="endpoint-state">
              {this.state.details.serialNumber || "-"}
            </div>
            <div></div>
            <div>
              <button
                disabled={
                  !this.state.details.online ||
                  this.state.pendingUpdate !== undefined
                }
                onClick={() => this.updateCoreSoftware(false)}
              >
                Update core software
              </button>
              <button
                disabled={
                  !this.state.details.online ||
                  this.state.pendingUpdate !== undefined
                }
                onClick={() => this.updateCoreSoftware(true)}
              >
                Update core software (DEBUG)
              </button>
              <button onClick={this.showRedeployInstructions}>
                {this.state.details.state === "NEW"
                  ? "Deploy by SSH"
                  : "Redeploy by SSH"}
              </button>
              <button onClick={this.deleteEndpoint} className="danger">
                Delete
              </button>
            </div>
          </div>
          <h3>Live Data:</h3>
          <LiveEndpointView
            endpointId={this.props.params!["endpointId"]!}
            onlineStateChangeCallback={this.onlineStateChanged}
            statusReceivedCallback={this.statusChanged}
            eventsReceivedCallback={this.eventsReceived}
            initialAppliances={this.state.details?.values}
            applianceStatuses={this.state.applianceStatuses}
            allowedApplianceIds={this.state.details?.description?.content?.processes?.flatMap((p) => [
              ...p.appliances.map((a) => a.id),
              ...(p.applianceReferences?.map((r) => r.id) ?? [])
            ])}
          />
          <h3>Events:</h3>
          <EventView
            ref={this.eventViewRef}
            endpointId={this.props.params!["endpointId"]!}
          />
          <h3>HAL Status</h3>
          <HalStatusView endpointId={this.state.endpointId} />
          <h3>HAL Configuration</h3>
          <div className="properties">
            <div>Status:</div>
            <div className="endpoint-state">
              {this.state.details.halConfiguration.pending
                ? "Pending"
                : "Applied"}
            </div>
            <div></div>
            <div>
              <button
                onClick={() => {
                  this.setState({ openEditor: Editor.ManualHalConfiguration });
                }}
              >
                HAL Configuration
              </button>
            </div>
          </div>
          {this.state.openEditor === Editor.ManualHalConfiguration && (
            <ConfigurationEditor
              subject="HAL Configuration"
              config={this.state.halConfiguration || {}}
              configPutUrl={
                getApiUrl() +
                "/v1/endpoint/" +
                this.state.endpointId +
                "/hal/configuration"
              }
              closeCallback={this.halConfigurationEditorClosed}
            />
          )}
          <h3>Endpoint Description</h3>
          <div className="properties">
            <div>Status:</div>
            <div className="endpoint-state">
              {this.state.details.description.pending ? "Pending" : "Applied"}
            </div>
            <div></div>
            <div>
              <button
                onClick={() => {
                  this.setState({ openEditor: Editor.ManualDescription });
                }}
              >
                Endpoint Description
              </button>
            </div>
          </div>
          <h3>Application Configuration</h3>
          <div className="properties">
            <div>Status:</div>
            <div className="endpoint-state">
              {this.state.details.applicationConfiguration.pending
                ? "Pending"
                : "Applied"}
            </div>
            <div></div>
            <div>
              <button
                onClick={() => {
                  this.setState({
                    openEditor: Editor.ManualApplicationConfiguration,
                  });
                }}
              >
                Application Configuration
              </button>
            </div>
          </div>
          {this.state.openEditor === Editor.ReDeploy && (
            <ReDeploy
              endpointId={this.state.endpointId}
              endpointState={this.state.details.state}
              endpointPlatform={this.state.details.platform}
              closeCallback={() => {
                this.setState({ openEditor: Editor.None });
              }}
            />
          )}
          {this.state.openEditor === Editor.ManualDescription && (
            <ConfigurationEditor
              subject="Endpoint Description"
              config={this.state.description || {}}
              configPutUrl={
                getApiUrl() +
                "/v1/endpoint/" +
                this.state.endpointId +
                "/description"
              }
              closeCallback={this.editorClosed}
            />
          )}
          {this.state.openEditor === Editor.ManualApplicationConfiguration && (
            <ConfigurationEditor
              subject="Application Configuration"
              config={this.state.applicationConfiguration || {}}
              configPutUrl={
                getApiUrl() +
                "/v1/endpoint/" +
                this.state.endpointId +
                "/applications"
              }
              closeCallback={this.applicationConfigurationEditorClosed}
            />
          )}
        </div>
      );
    } else {
      return <div className="contoller-details loading">Loading...</div>;
    }
  }

  private onlineStateChanged = (online: boolean) => {
    if (this.state.details && this.state.details.online === online) {
      return;
    }
    if (this.state.pendingUpdate) {
      if (!online) {
        this.state.pendingUpdate.resolve();
        this.setState({
          pendingUpdate: undefined,
        });
      }
    } else {
      if (online) {
        toast.success("Endpoint is online");
      } else {
        toast.error("Endpoint is offline");
      }
    }
    if (this.state.details) {
      this.setState({
        ...this.state,
        details: {
          ...this.state.details,
          online: online,
        },
      });
    }
  };

  private statusChanged = (message: string) => {
    const status = JSON.parse(message) as EndpointStatusMessage;
    if (status.state === "UPDATING") {
      this.state.pendingUpdate?.resolve();
      this.setState({
        pendingUpdate: undefined,
        applianceStatuses: status.appliances,
      });
    } else {
      this.setState({
        applianceStatuses: status.appliances,
      });
    }
    console.log("Status changed: " + message);
    setTimeout(() => {
      this.getEndpoint();
      this.getEndpointDescription();
      this.getHALConfiguration();
    }, 500);
  };

  private eventsReceived = (message: string) => {
    this.eventViewRef.current?.prependEvents(message);
  };

    private updateCoreSoftware = (debug: boolean) => {
        fetch(getApiUrl() + "/v1/endpoint/" + this.state.endpointId + "/coreUpdate?debug=" + (debug ? "true" : "false"), {
            method: "POST",
            headers: [
                ["Accept", "application/json"],
                ["Authorization", authService.authHeader()]
            ]
        })
            .then(response => {
                this.setState({
                    pendingUpdate: new PendingUpdate(this.coreSoftwareUpdateFailed)
                })
            })
            .catch((error) => {
                console.error(error);
            });
    }

  private coreSoftwareUpdateFailed = () => {
    this.setState({
      pendingUpdate: undefined,
    });
  };

  private halConfigurationEditorClosed = () => {
    this.setState({
      openEditor: Editor.None,
    });
    this.getEndpoint();
    this.getHALConfiguration();
  };

  private editorClosed = () => {
    this.setState({
      openEditor: Editor.None,
    });
    this.getEndpoint();
    this.getEndpointDescription();
  };

  private applicationConfigurationEditorClosed = () => {
    this.setState({
      openEditor: Editor.None,
    });
    this.getEndpoint();
  };
}

const HOCEndpoint = withRouter(Endpoint);
export default HOCEndpoint;
